import { FocusMonitor } from '@angular/cdk/a11y'
import { coerceBooleanProperty } from '@angular/cdk/coercion'
import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit, Optional, Self,
  SimpleChanges,
} from '@angular/core'
import { FormGroupDirective, NgControl, NgForm } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { MatFormFieldControl } from '@angular/material/form-field'
import { ClinicService, ProductCategory, ProductModel } from '@mmx/shared'
import { filter, intersection } from 'lodash'

import { ChipsSelectorInputComponent } from '../chips-selector'
import { ProductsSelectorDialogComponent } from './products-selector-dialog.component'

@Component({
  selector: 'mmx-products-selector-input',
  templateUrl: '../chips-selector/chips-selector.component.html',
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: ProductsSelectorInputComponent,
    },
  ],
  standalone: false,
})
export class ProductsSelectorInputComponent
  extends ChipsSelectorInputComponent<ProductModel>
  implements OnInit, OnChanges
{
  @Input() categories: (string | ProductCategory)[]
  @Input() includeCategories: boolean = true
  protected _placeholder = 'Procedures'

  @Input()
  get quickAddByCodeEnabled() {
    return this._quickAdd
  }
  set quickAddByCodeEnabled(enabled: boolean) {
    this._quickAdd = coerceBooleanProperty(enabled)
    this.stateChanges.next()
  }
  protected _quickAdd = false

  private products: ProductModel[] = []

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    @Optional() protected parentForm: NgForm,
    @Optional() protected parentFormGroup: FormGroupDirective,
    protected fm: FocusMonitor,
    protected elRef: ElementRef,
    private clinicService: ClinicService,
    private dialog: MatDialog,
  ) {
    super(ngControl, parentForm, parentFormGroup, fm, elRef)
  }

  ngOnInit() {
    super.ngOnInit()

    this.clinicService.getProducts().subscribe((products) => {
      this.products = products
      this.filterProducts()
    })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.categories || changes.includeCategories) {
      this.filterProducts()
    }
  }

  filterProducts() {
    let products = this.products

    if (this.categories?.length > 0) {
      const categories = this.categories.map((cat) =>
        cat instanceof ProductCategory ? cat.id : cat,
      )
      products = filter(
        products,
        (p) => {
          return this.includeCategories ?
            intersection(categories, p.categories).length > 0 : intersection(categories, p.categories).length === 0
        },
      )
    }

    this.setAvailableItems(products, ['code', 'category', 'name'])
  }

  protected onItemSelected(product: ProductModel): void {
    if (this._quickAdd && product.code && product.code.length > 0) {
      const productsWithSameCode = this.availableItems.filter(
        (p) => p.id !== product.id && p.code === product.code,
      )
      if (productsWithSameCode.length > 0) {
        const dialogRef = this.dialog.open(ProductsSelectorDialogComponent, {
          width: '550px',
          data: {
            products: productsWithSameCode,
          },
        })

        dialogRef.afterClosed().subscribe((selected) => {
          const componentValue = this.value || []
          selected.forEach((p) => componentValue.push(p))
          this.value = componentValue
        })
      }
    }
  }
}
